import React, { SyntheticEvent, useEffect, useState } from 'react';
import ReactS3Uploader, {
  ReactS3UploaderProps,
  S3Response,
} from 'react-s3-uploader';
import { useDropzone } from 'react-dropzone';
import { apiOrgLive } from 'services/api';
import { AxiosResponse } from 'axios';
import {
  FieldValues,
  RegisterOptions,
  UseFormProps,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import mime from 'mime-types';
import LocalSpinner from 'components/spinners/local';
import ErrorMessage from 'components/errorMessage';
import { Card, CardContent } from 'components/ui/card';
import { Icon } from 'components/ui/icon';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';

export interface IFileArray {
  url?: string;
  uploadId?: string;
  fileObj?: File;
}
export interface IMultipleFileProps {
  fileArray: IFileArray[];
  isLoading: boolean;
}

export type documentDetails = {
  upload_id: string;
  url: string;
  name: string;
  type: string;
  updated_at?: string;
};

interface IProps {
  elemId: string;
  errors: UseFormProps['errors'];
  setError: UseFormSetError<any>;
  setValue: UseFormSetValue<FieldValues>;
  register: UseFormRegister<FieldValues>;
  validation: RegisterOptions;
  onBlur: () => void;
  fileProps: IFileArray[];
  setFileProps: (arg: IMultipleFileProps | undefined) => void;
  s3UploaderProps?: ReactS3UploaderProps;
  subText?: string | React.ReactElement;
  infoElement?: React.ReactElement;
  multipleUpload?: boolean;
  kybId: string;
  fileLimit?: number;
  setIsCleared: (arg: boolean) => void;
}

export const isAnyFileListEmpty = (files: FileList[]) => {
  return !files.every((f) => f?.length > 0);
};

export const returnFilteredList = (list: IFileArray[]) => {
  return list.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t.fileObj?.name && value.url === t.url)
  );
};

export const getMultipleFileS3Props = (fileProps: IFileArray[]) => {
  const filteredArray = returnFilteredList(fileProps);
  const fileArray: documentDetails[] = [];
  const array =
    filteredArray?.length > 0
      ? filteredArray.forEach((file) => {
          if (file?.fileObj && file?.uploadId) {
            fileArray.push({
              name: file.fileObj?.name,
              url: file?.url || '',
              type: mime.contentType(file.fileObj?.name || '') || '',
              upload_id: file.uploadId || '',
              updated_at: new Date().toISOString(),
            });
          }
        })
      : [];
  return fileArray;
};

const InputMultipleFileUpload = ({
  elemId,
  setValue,
  errors,
  setError,
  register,
  validation,
  onBlur,
  subText,
  fileProps,
  setFileProps,
  s3UploaderProps,
  infoElement,
  multipleUpload = false,
  setIsCleared,
  kybId,
  fileLimit,
}: IProps) => {
  const [uploadKey, setUploadKey] = useState('');
  const [uploadStatus, setUploadStatus] = useState({
    show: false,
    status: '',
    percent: 0,
  });
  const { ref, ...registerField } = register(elemId, validation);

  const deleteDocument = async (upload_id: string | undefined) => {
    try {
      if (upload_id) await apiOrgLive.remove(`/uploads/${upload_id}`);
    } catch (e) {
      console.log(e);
    }
  };

  const [len, setLen] = useState(false);

  const fileLengthCheck = (fileList: IFileArray[] | any) => {
    const randomString = Math.random().toString(36);
    if (fileLimit && fileList.length > fileLimit) {
      setUploadKey(randomString);
      setError(elemId, {
        type: 'custom',
        message: 'Please upload maximum of ' + fileLimit + ' documents.',
      });
      return false;
    }
    return true;
  };

  useEffect(() => {
    const fileInput = document.getElementById(elemId) as HTMLInputElement;
    // check for url and name of file and prefill only if they exist
    if (fileProps?.length > 0 && fileProps?.[0].fileObj) {
      const dataTransfer =
        new ClipboardEvent('').clipboardData || new DataTransfer();
      // loop over this for prefilling
      fileProps.forEach((file) => {
        if (file.fileObj) {
          dataTransfer.items.add(file.fileObj);
        }
      });
      fileInput.files = dataTransfer.files;
    }
  }, [fileProps]);

  const deleteExistingFile = () => {
    fileProps.forEach((file) => {
      if (file?.uploadId) {
        deleteDocument(file.uploadId);
      }
    });
  };

  const isFileValid = (file: File) => {
    const acceptedMimeTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    const maxFileSize = 10 * 1024 * 1024;
    const randomString = Math.random().toString(36);

    if (!acceptedMimeTypes.includes(file.type)) {
      setUploadKey(randomString);
      setError(elemId, {
        type: 'custom',
        message: 'Please upload a document of type pdf, jpeg or png',
      });
      return false;
    }

    if (file.size > maxFileSize) {
      setUploadKey(randomString);
      setError(elemId, {
        type: 'custom',
        message: 'Please upload a document of size less than 10MB.',
      });
      return false;
    }
    return true;
  };

  const getSignedUrl = async (
    file: File,
    callback: (params: S3Response) => void
  ) => {
    deleteExistingFile();
    if (!len) {
      if (isFileValid(file)) {
        const params = {
          file_name: `${file.name.split('.')[0]}_${elemId}.${file.name.slice(
            file.name.lastIndexOf('.') + 1
          )}`,
          file_type: mime.contentType(file.name) as string,
          file_size: file.size,
          name: elemId,
        };
        try {
          const response: AxiosResponse = await apiOrgLive.post(
            `/uploads/sign?kyb_id=${kybId}`,
            params
          );

          fileProps.push({
            ...fileProps,
            url: response.data.object_url,
          });

          const filteredList = returnFilteredList(fileProps);
          if (!fileLengthCheck(filteredList)) {
            // setUploadStatus({ show: false, status: '', percent: 0 });
            // setFileProps(undefined);
            return;
          }
          setFileProps({ fileArray: filteredList, isLoading: true });

          const signedResponse: S3Response = {
            signedUrl: response.data.signed_url,
            publicUrl: response.data.object_url || '',
            filename: file.name,
            fileKey: response.data.success_token,
          };

          callback(signedResponse);
        } catch (e) {
          console.log(e);
        }
      } else {
        setUploadStatus({ show: false, status: '', percent: 0 });
        setFileProps(undefined);
      }
    } else {
      setLen(false);
      const randomString = Math.random().toString(36);
      setUploadKey(randomString);
      setError(elemId, {
        type: 'custom',
        message: 'Please upload maximum of ' + fileLimit + ' documents.',
      });
      setUploadStatus({ show: false, status: '', percent: 0 });
      setFileProps(undefined);
    }
  };

  const onUploadFinish = async (result: S3Response, file: File) => {
    const uploaded: AxiosResponse = await apiOrgLive.get(
      `/uploads/confirm?success_token=${result.fileKey}`
    );
    if (uploaded) {
      fileProps.push({
        ...fileProps,
        url: result.publicUrl,
        fileObj: file,
        uploadId: uploaded?.data?.id,
      });
      const filteredList = returnFilteredList(fileProps);
      if (!fileLengthCheck(filteredList)) {
        // setUploadStatus({ show: false, status: '', percent: 0 });
        // setFileProps(undefined);
        return;
      }
      setFileProps({ fileArray: filteredList, isLoading: false });
      setIsCleared?.(false);
    }
  };

  const onProgress = (percent: number, status: string, _: File) => {
    setUploadStatus({
      show: true,
      status,
      percent,
    });

    const filteredList = returnFilteredList(fileProps);
    if (!fileLengthCheck(filteredList)) {
      // setUploadStatus({ show: false, status: '', percent: 0 });
      // setFileProps(undefined);
      return;
    }
    setFileProps({ fileArray: filteredList, isLoading: true });
  };

  const clearFile = () => {
    fileProps.forEach((file) => {
      if (file?.uploadId) {
        deleteDocument(file.uploadId);
      }
    });
    setUploadStatus({ ...uploadStatus, show: false });
    setValue(elemId, undefined);
    onBlur();
    setFileProps(undefined);
    setUploadKey(Math.random().toString(36));
    setIsCleared?.(true);
  };

  const onError = (message: string) => {
    setUploadStatus({ ...uploadStatus, show: false });
    const filteredList = returnFilteredList(fileProps);
    if (!fileLengthCheck(filteredList)) {
      // setUploadStatus({ show: false, status: '', percent: 0 });
      // setFileProps(undefined);
      return;
    }
    setFileProps({ fileArray: filteredList, isLoading: false });
    clearFile();
    setError(elemId, {
      type: 'custom',
      message: `${message} Please try again`,
    });
  };

  useEffect(() => {
    if (uploadStatus.status === 'Upload completed') {
      setTimeout(() => setUploadStatus({ ...uploadStatus, show: false }), 5000);
    }
  }, [uploadStatus.status]);

  const fileSizeConverter = (fileSize: number | undefined) => {
    const marker = 1024;
    const decimal = 2;
    const kiloBytes = marker;
    const megaBytes = marker * marker;
    const gigaBytes = marker * marker * marker;

    if (fileSize) {
      if (fileSize < kiloBytes) return fileSize + ' Bytes';
      else if (fileSize < megaBytes)
        return (fileSize / kiloBytes).toFixed(decimal) + ' KB';
      else if (fileSize < gigaBytes)
        return (fileSize / megaBytes).toFixed(decimal) + ' MB';
      else return fileSize + 'Bytes';
    } else return 0 + 'Bytes';
  };

  const totalFileSize = () => {
    let size = 0;
    fileProps.forEach((file) => {
      size += file.fileObj?.size || 0;
    });
    return fileSizeConverter(size);
  };

  const numberCheck = (e: any) => {
    setLen(false);
    if (!fileLengthCheck(e.files)) {
      setLen(true);
    }
  };

  const { getRootProps, isDragActive } = useDropzone({
    multiple: multipleUpload,
    accept: {
      'application/pdf': [],
    },
    noClick: true,
    onDrop: (acceptedFiles) => {
      const fileInput = document.getElementById(elemId) as HTMLInputElement;
      if (fileInput && acceptedFiles.length > 0) {
        const dt = new DataTransfer();
        acceptedFiles.forEach((file) => dt.items.add(file));
        fileInput.files = dt.files;
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
  });

  return (
    <>
      {/* Updated to use react-dropzone and have an outlined area */}
      <div
        {...getRootProps()}
        className={cn(
          'rounded-lg border border-dashed border-gray-blue-30 flex flex-col p-4 items-center gap-1 self-stretch cursor-pointer mt-2',
          isDragActive && 'border-green-400 bg-green-100'
        )}
        onClick={() => {
          const input = document.getElementById(elemId) as HTMLInputElement;
          if (input) input.click();
        }}
      >
        <div className="flex flex-col items-center justify-center">
          <span className="text-[40px] w-10 h-10 leading-none inline-block mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="41"
              height="40"
              viewBox="0 0 41 40"
              fill="none"
            >
              <path
                d="M20.5 3.75C17.2861 3.75 14.1443 4.70305 11.472 6.48862C8.79969 8.27419 6.71689 10.8121 5.48696 13.7814C4.25704 16.7507 3.93524 20.018 4.56225 23.1702C5.18926 26.3224 6.73692 29.2179 9.00952 31.4905C11.2821 33.7631 14.1776 35.3107 17.3298 35.9378C20.482 36.5648 23.7493 36.243 26.7186 35.013C29.6879 33.7831 32.2258 31.7003 34.0114 29.028C35.797 26.3557 36.75 23.2139 36.75 20C36.7417 15.6928 35.027 11.5643 31.9814 8.51864C28.9357 5.47296 24.8072 3.75826 20.5 3.75ZM26.6875 19.9375C26.5711 20.0546 26.4322 20.1468 26.2791 20.2086C26.126 20.2704 25.9619 20.3004 25.7969 20.2969C25.6342 20.3001 25.4726 20.2699 25.3221 20.2081C25.1715 20.1463 25.0353 20.0542 24.9219 19.9375L21.75 16.7656V26.25C21.75 26.5815 21.6183 26.8995 21.3839 27.1339C21.1495 27.3683 20.8315 27.5 20.5 27.5C20.1685 27.5 19.8505 27.3683 19.6161 27.1339C19.3817 26.8995 19.25 26.5815 19.25 26.25V16.7656L16.0781 19.9375C15.8404 20.1529 15.5288 20.2687 15.2081 20.2608C14.8873 20.2529 14.5818 20.122 14.3549 19.8951C14.128 19.6682 13.9971 19.3627 13.9892 19.042C13.9813 18.7212 14.0971 18.4097 14.3125 18.1719L19.6094 12.875C19.7265 12.7572 19.8642 12.6619 20.0156 12.5938C20.3264 12.4686 20.6736 12.4686 20.9844 12.5938C21.1358 12.6619 21.2735 12.7572 21.3906 12.875L26.6875 18.1719C26.9213 18.4062 27.0526 18.7237 27.0526 19.0547C27.0526 19.3857 26.9213 19.7032 26.6875 19.9375Z"
                fill="#666E7A"
              />
            </svg>
          </span>
          <p className="text-gray-blue-80 text-sm font-medium leading-5 pb-1">
            Upload files(s)
          </p>
          {fileLimit && (
            <p className="text-gray-blue-60 text-sm font-normal leading-5">
              Maximum of {fileLimit} files can be uploaded.
            </p>
          )}
          <div className="pt-4 flex justify-center items-center gap-1 self-stretch">
            <Button
              type="button"
              variant="link"
              className="text-dark-green-link text-sm font-medium leading-5 h-auto p-0"
              onClick={() => {
                const input = document.getElementById(
                  elemId
                ) as HTMLInputElement;
                if (input) input.click();
              }}
            >
              Choose files
            </Button>
            <p className="text-gray-blue-60 text-sm font-normal leading-5">
              {' '}
              one by one, or drag and drop one by one
            </p>
          </div>
          {/* Hidden input from ReactS3Uploader */}
          <ReactS3Uploader
            id={elemId}
            style={{ display: 'none' }}
            disabled={uploadStatus.show || fileProps.length > 0}
            getSignedUrl={getSignedUrl}
            onFinish={onUploadFinish}
            onInput={(e: SyntheticEvent) => numberCheck(e.currentTarget)}
            key={uploadKey}
            multiple={multipleUpload}
            className={cn(
              errors?.[elemId] && 'border-red-400',
              uploadStatus.show && 'cursor-not-allowed text-gray-blue-30'
            )}
            inputRef={ref}
            {...registerField}
            accept="application/pdf"
            onProgress={onProgress}
            onError={onError}
            uploadRequestHeaders={{}}
            contentDisposition="auto"
            {...s3UploaderProps}
          />
        </div>
      </div>

      {infoElement}
      {subText && (
        <div className="mt-1 text-sm font-normal leading-5 text-gray-blue-60">
          {subText}
        </div>
      )}
      {errors?.[elemId] && (
        <ErrorMessage
          isValidation
          message={
            errors?.[elemId]?.message?.toString() ||
            'Please upload the required documents'
          }
          preIcon={
            <Icon name="alert-circle" className="text-red-500" size={16} />
          }
          testId={elemId}
        />
      )}
      {fileProps.length > 0 && (
        <div className="mt-2 bg-gray-blue-0 border border-gray-blue-20 rounded-lg">
          <Card className="w-full flex text-sm mb-3 border-0 shadow-none bg-transparent rounded-none">
            <CardContent className="flex items-center p-4">
              <span className="w-full">
                {fileProps.map((file, index) => (
                  <div className="flex justify-between mt-2" key={index}>
                    <span className="inline-flex items-center">
                      <span className="flex items-center justify-center rounded-full w-max h-max border-4 border-gray-blue-2 bg-gray-blue-5 mr-2">
                        <Icon
                          name="file"
                          className="text-gray-blue-50"
                          size={24}
                        />
                      </span>
                      <span className="text-sm font-normal leading-5 text-gray-blue-70">
                        {file.fileObj?.name}
                        <br />
                        <span className="text-gray-blue-50 text-xs font-normal leading-4">
                          {fileSizeConverter(file?.fileObj?.size)}
                        </span>
                      </span>
                    </span>
                    {index === 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => clearFile()}
                        className="text-gray-blue-50 hover:text-gray-blue-80 hover:bg-transparent"
                      >
                        <Icon name="x" size={20} />
                      </Button>
                    )}
                  </div>
                ))}
              </span>
            </CardContent>
          </Card>
        </div>
      )}
      {uploadStatus.show && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-normal leading-5 text-gray-blue-60">
            {uploadStatus.status === 'Upload completed'
              ? totalFileSize()
              : uploadStatus.status}{' '}
            - {uploadStatus.percent}% uploaded
          </span>
          {uploadStatus.status === 'Upload completed' ? (
            <Icon name="check-circle" className="text-green-500" size={24} />
          ) : (
            <LocalSpinner size="sm" color="primary" isLoading />
          )}
        </div>
      )}
    </>
  );
};

export default InputMultipleFileUpload;
